import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import type { ClientLogging } from "../client/client-logging";
import { makeContextProvider } from "../client/client-logging";
import type { EarlyErrorCapture } from "../client/early-error-capture";
import type { VisitIdSource } from "../client/visit-id";
import { PerformanceTelemetry } from "../performance-telemetry";
import { WebVitals } from "../web-vitals";
import { LoggerContext } from "./logger-context";

export interface LoggerProviderProps {
  children: ReactNode;
  logging: ClientLogging;
  errorCapture: Pick<EarlyErrorCapture, "setUnhandledErrorReporter">;
  visits: Pick<VisitIdSource, "trackVisitActivity">;
  resolveLeadId?: () => string | undefined;
}

// Mints sessionId in a useState initializer (not an effect) so it is on the first event. User identity stays out of this provider: below an auth provider it would sit behind an async gate, and a context bridge would re-render every consumer; authenticated callers use useLogger({ userId }).
export function LoggerProvider({
  children,
  logging,
  errorCapture,
  visits,
  resolveLeadId,
}: LoggerProviderProps) {
  const [sessionId] = useState(logging.getSessionId);
  // Resolved in a useState initializer so the lead is on the very first event. The resolver must be idempotent (StrictMode) and must not throw: this sits above the router, so a throw would blank the app.
  const [leadId] = useState(() => {
    const resolved = resolveLeadId?.();
    return resolved === "" ? undefined : resolved;
  });
  const { createLogger } = logging;

  const log = useMemo(
    () => createLogger(makeContextProvider(sessionId, undefined, leadId)),
    [createLogger, sessionId, leadId],
  );

  // Adopt the module-scope boundary rather than installing listeners (that would double-report). The disposer returns it to the early pre-mount path on unmount.
  const { setUnhandledErrorReporter } = errorCapture;
  useEffect(
    () =>
      setUnhandledErrorReporter((error, source) => {
        log.error("infra.error.unhandled", error, { source }, { stack: true });
      }),
    [setUnhandledErrorReporter, log],
  );

  const { trackVisitActivity } = visits;
  useEffect(() => trackVisitActivity(), [trackVisitActivity]);

  return (
    <LoggerContext value={{ sessionId, leadId, createLogger }}>
      <WebVitals logger={log} />
      <PerformanceTelemetry logger={log} />
      {children}
    </LoggerContext>
  );
}
